import { useCallback } from "react";

// Helpers
import { getPositionFamily, supportedSystemPositions } from "@/Common/helpers/Positions";

//****************************************** Sales Associate ******************************************
import { getSalesWashoutReview, getSalesAssociateWashout } from "../api/Departments/sales/queries";
import {
	approveAllSalesWashout,
	generateSalesWashout,
	updateSalesWashout,
	updateSalesWashoutStatus,
} from "../api/Departments/sales/mutations";
import {
	getOnCreateSalesAssociateWashout,
	getOnUpdateSalesAssociateWashout,
	getOnUpdateSalesAssociateWashoutStatus,
} from "../api/Departments/sales/subscriptions";

//****************************************** Finance ******************************************
import { getFinanceWashout, getFinanceWashoutReview } from "../api/Departments/finance/queries";
import {
	updateFinanceWashout,
	updateFinanceWashoutStatus,
	approveAllFinanceWashout,
	generateFinanceWashout,
} from "../api/Departments/finance/mutations";
import {
	getOnUpdateFinanceWashout,
	getOnUpdateFinanceWashoutStatus,
	getOnNewFinanceWashoutAvailable,
} from "../api/Departments/finance/subscriptions";

//****************************************** Sales Departmental ******************************************
import { getSalesDepartmentalWashout, getSalesDepartmentalWashoutReview } from "../api/Departments/managers/sales/queries";
import {
	updateSalesDepartmentalWashout,
	updateSalesDepartmentalWashoutStatus,
	approveAllSalesDepartmentalWashout,
	generateSalesDepartmentalWashout,
} from "../api/Departments/managers/sales/mutations";
import {
	getOnUpdateSalesDepartmentalWashout,
	getOnUpdateSalesDepartmentalWashoutStatus,
	getOnNewSalesDepartmentalWashoutAvailable,
} from "../api/Departments/managers/sales/subscriptions";

//****************************************** Service Departmental ******************************************
import { getServiceDepartmentalWashout, getServiceDepartmentalWashoutReview } from "../api/Departments/managers/departmental/queries";
import {
	updateServiceDepartmentalWashout,
	updateServiceDepartmentalWashoutStatus,
	approveAllServiceDepartmentalWashout,
	generateServiceDepartmentalWashout,
} from "../api/Departments/managers/departmental/mutations";
import {
	getOnUpdateServiceDepartmentalWashout,
	getOnUpdateServiceDepartmentalWashoutStatus,
	getOnNewServiceDepartmentalWashoutAvailable,
} from "../api/Departments/managers/departmental/subscriptions";

//****************************************** Technicians ******************************************
import { getTechniciansWashout, getTechniciansWashoutReview } from "../api/Departments/technicians/queries";
import {
	approveAllTechniciansWashout,
	generateTechniciansWashout,
	updateTechniciansWashout,
	updateTechniciansWashoutStatus,
} from "../api/Departments/technicians/mutations";

import {
	getOnCreateTechniciansWashout,
	getOnUpdateTechniciansWashout,
	getOnUpdateTechniciansWashoutStatus,
} from "../api/Departments/technicians/subscriptions";

//****************************************** Advisors ******************************************
import { getAdvisorsWashout, getAdvisorsWashoutReview } from "../api/Departments/advisors/queries";
import {
	approveAllAdvisorsWashout,
	generateAdvisorsWashout,
	updateAdvisorsWashout,
	updateAdvisorsWashoutStatus,
} from "../api/Departments/advisors/mutations";
import {
	getOnCreateAdvisorsWashout,
	getOnUpdateAdvisorsWashout,
	getOnUpdateAdvisorsWashoutStatus,
} from "../api/Departments/advisors/subscriptions";

// Constants
const POSITIONS = supportedSystemPositions();

const MAP_POSITIONS_QUERIES = {
	SALES: {
		getWashout: getSalesAssociateWashout,
		getWashouts: null,
		getWashoutReview: getSalesWashoutReview,
		updateWashout: updateSalesWashout,
		updateWashoutStatus: updateSalesWashoutStatus,
		approveWashouts: approveAllSalesWashout,
		generateWashout: generateSalesWashout,
	},
	FINANCE: {
		getWashout: getFinanceWashout,
		getWashouts: null,
		getWashoutReview: getFinanceWashoutReview,
		updateWashout: updateFinanceWashout,
		updateWashoutStatus: updateFinanceWashoutStatus,
		approveWashouts: approveAllFinanceWashout,
		generateWashout: generateFinanceWashout,
	},
	SALES_DEPARTMENTAL: {
		getWashout: getSalesDepartmentalWashout,
		getWashouts: null,
		getWashoutReview: getSalesDepartmentalWashoutReview,
		updateWashout: updateSalesDepartmentalWashout,
		updateWashoutStatus: updateSalesDepartmentalWashoutStatus,
		approveWashouts: approveAllSalesDepartmentalWashout,
		generateWashout: generateSalesDepartmentalWashout,
	},
	SERVICE_DEPARTMENTAL: {
		getWashout: getServiceDepartmentalWashout,
		getWashouts: null,
		getWashoutReview: getServiceDepartmentalWashoutReview,
		updateWashout: updateServiceDepartmentalWashout,
		updateWashoutStatus: updateServiceDepartmentalWashoutStatus,
		approveWashouts: approveAllServiceDepartmentalWashout,
		generateWashout: generateServiceDepartmentalWashout,
	},
	TECHNICIANS: {
		getWashout: getTechniciansWashout,
		getWashouts: null,
		getWashoutReview: getTechniciansWashoutReview,
		updateWashout: updateTechniciansWashout,
		updateWashoutStatus: updateTechniciansWashoutStatus,
		approveWashouts: approveAllTechniciansWashout,
		generateWashout: generateTechniciansWashout,
	},
	ADVISORS: {
		getWashout: getAdvisorsWashout,
		getWashouts: null,
		getWashoutReview: getAdvisorsWashoutReview,
		updateWashout: updateAdvisorsWashout,
		updateWashoutStatus: updateAdvisorsWashoutStatus,
		approveWashouts: approveAllAdvisorsWashout,
		generateWashout: generateAdvisorsWashout,
	},
};

const MAP_POSITIONS_SUBSCRIPTIONS = {
	SALES: {
		onCreateWashout: {
			query: getOnCreateSalesAssociateWashout,
			getter: "onNewSalesWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateSalesAssociateWashout,
			getter: "onUpdateSalesWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateSalesAssociateWashoutStatus,
			getter: "onUpdateSalesWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
	FINANCE: {
		onCreateWashout: {
			query: getOnNewFinanceWashoutAvailable,
			getter: "onNewFinanceWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateFinanceWashout,
			getter: "onUpdateFinanceWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateFinanceWashoutStatus,
			getter: "onUpdateFinanceWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
	SALES_DEPARTMENTAL: {
		onCreateWashout: {
			query: getOnNewSalesDepartmentalWashoutAvailable,
			getter: "onNewSalesDepartmentalWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateSalesDepartmentalWashout,
			getter: "onUpdateSalesDepartmentalWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateSalesDepartmentalWashoutStatus,
			getter: "onUpdateSalesDepartmentalWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
	SERVICE_DEPARTMENTAL: {
		onCreateWashout: {
			query: getOnNewServiceDepartmentalWashoutAvailable,
			getter: "onNewServiceDepartmentalWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateServiceDepartmentalWashout,
			getter: "onUpdateServiceDepartmentalWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateServiceDepartmentalWashoutStatus,
			getter: "onUpdateServiceDepartmentalWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
	TECHNICIANS: {
		onCreateWashout: {
			query: getOnCreateTechniciansWashout,
			getter: "onNewTechniciansWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateTechniciansWashout,
			getter: "onUpdateTechniciansWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateTechniciansWashoutStatus,
			getter: "onUpdateTechniciansWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
	ADVISORS: {
		onCreateWashout: {
			query: getOnCreateAdvisorsWashout,
			getter: "onNewAdvisorsWashoutAvailable",
		},
		onUpdateWashout: {
			query: getOnUpdateAdvisorsWashout,
			getter: "onUpdateAdvisorsWashout",
		},
		onUpdateWashoutStatus: {
			query: getOnUpdateAdvisorsWashoutStatus,
			getter: "getOnUpdateAdvisorsWashoutStatus",
		},
		onNewWashoutAvailable: { query: "", getter: "" },
	},
};

const getPositionSubscriptions = (position, positionType = "Producer") => {
	const subscriptions = new Map([
		["onCreateWashout", { query: "", getter: "" }],
		["onNewWashoutAvailable", { query: "", getter: "" }],
		["onUpdateWashout", { query: "", getter: "" }],
		["onUpdateWashoutStatus", { query: "", getter: "" }],
	]);

	const isDepartmental = positionType === "Departmental";
	const positionFamily = getPositionFamily(position, isDepartmental);
	const positionSubscriptions = MAP_POSITIONS_SUBSCRIPTIONS[positionFamily];

	if (!positionSubscriptions) {
		return subscriptions;
	}

	subscriptions.set("onCreateWashout", positionSubscriptions.onCreateWashout);
	subscriptions.set("onUpdateWashout", positionSubscriptions.onUpdateWashout);
	subscriptions.set("onUpdateWashoutStatus", positionSubscriptions.onUpdateWashoutStatus);
	return subscriptions;
};

const getQueriesByPosition = (position, positionType = "Producer") => {
	const isDepartmental = positionType === "Departmental";
	const positionFamily = getPositionFamily(position, isDepartmental);

	return (
		MAP_POSITIONS_QUERIES[positionFamily] ?? {
			getWashout: null,
			getWashouts: null,
			getWashoutReview: null,
			updateWashout: null,
			updateWashoutStatus: null,
			approveWashouts: null,
		}
	);
};

function useFactoryWashout() {
	const useWashoutQueries = useCallback((position, positionType = "Producer") => getQueriesByPosition(position, positionType), []);

	const useWashoutQueriesPositions = useCallback((positions = []) => {
		return positions.reduce((acc, position) => {
			acc[position.position] = getQueriesByPosition(position.position, position.positionType);
			return acc;
		}, {});
	}, []);

	const useWashoutSubscriptions = useCallback((position, positionType) => getPositionSubscriptions(position, positionType), []);

	const useWashoutSubscriptionsPositions = useCallback((positions = []) => {
		return positions.reduce((acc, position) => {
			acc[position.position] = getPositionSubscriptions(position.position, position.positionType);
			return acc;
		}, {});
	}, []);

	return { useWashoutQueries, useWashoutQueriesPositions, useWashoutSubscriptions, useWashoutSubscriptionsPositions };
}

export { useFactoryWashout, getPositionSubscriptions };
